import { z } from 'zod'
import { ATTACH_ARTICLES_MAX } from './article-addition'
import { insightLabelFieldSchema, INSIGHT_DIMENSIONS, insightSpecSchema } from './insight-spec'
import { sectionViewSchema } from './section-views'
import {
  STREAM_HITS_CAP,
  removeStreamHitsSchema,
  streamFiltersSchema,
  streamHitsQuerySchema,
} from './stream'

/** The model's side of an insight: the label profile it is shown, the summaries it writes, and
 *  the request and response of a console turn. */

// ── The label profile (what the model is shown instead of rows) ───────────────────────────────

export const INSIGHT_PROFILE_TOP = 30
export const INSIGHT_PROFILE_TOP_HEADLINES = 10
/** At most this many of the analytics build's columns are profiled — the widest-carried first. */
export const INSIGHT_PROFILE_FIELDS_MAX = 24

export const insightProfileSchema = z.object({
  /** Where the rows came from — a Library selection, or the Analytics page's filtered view. */
  scope: z.enum(['selection', 'view']),
  rowCount: z.number().int().min(0),
  /** yyyy-mm-dd of the oldest and newest row; null when there are no rows. */
  dateFrom: z.string().max(10).nullable(),
  dateTo: z.string().max(10).nullable(),
  totalReach: z.number().min(0),
  /** How many rows carry a sentiment / a classification — sentiment measures over unscored rows
   *  mean nothing, and the model should say so. */
  scored: z.object({ sentiment: z.number().int().min(0), classification: z.number().int().min(0) }),
  dimensions: z
    .array(
      z.object({
        field: insightLabelFieldSchema,
        distinct: z.number().int().min(0),
        top: z
          .array(z.object({ value: z.string().max(200), count: z.number().int().min(0) }))
          .max(INSIGHT_PROFILE_TOP),
      }),
    )
    .max(INSIGHT_DIMENSIONS.length + INSIGHT_PROFILE_FIELDS_MAX),
  /**
   * The analytics build's figure columns (EMV, a theme score…) the rows carry: what `sum:<key>` /
   * `avg:<key>` measure. Their label columns are among `dimensions`, as `field:<key>`.
   */
  figures: z
    .array(
      z.object({
        key: z.string().max(80),
        label: z.string().max(200),
        /** Rows that carry a figure. */
        present: z.number().int().min(0),
      }),
    )
    .max(INSIGHT_PROFILE_FIELDS_MAX)
    .default([]),
})
export type InsightProfile = z.infer<typeof insightProfileSchema>

// ── Written summaries ─────────────────────────────────────────────────────────────────────────

/** A written briefing on the coverage in scope — what Coverage Lab writes when asked to summarise.
 *  Every number in it must come from `summarizeCoverage`'s facts or a checked chart. */
export const insightSummarySchema = z.object({
  /** The story in a few words — "Recall coverage peaked mid-week and turned negative". */
  title: z.string().trim().min(1).max(100),
  /** Two or three short paragraphs: what happened, what moved, what it means. */
  overview: z.string().trim().min(1).max(1500),
  /** Three to six one-line findings, each carrying its number. */
  points: z.array(z.string().trim().min(1).max(300)).max(6).default([]),
})
export type InsightSummaryText = z.infer<typeof insightSummarySchema>

/** What a summary was written from — set by the server from the rows, never by the model. */
export const insightSummaryBasisSchema = z.object({
  articles: z.number().int().min(0),
  from: z.string().nullable(),
  to: z.string().nullable(),
})

export const insightSummaryOutSchema = insightSummarySchema.extend({
  basis: insightSummaryBasisSchema,
})
export type InsightSummary = z.infer<typeof insightSummaryOutSchema>

// ── The wire ───────────────────────────────────────────────────────────────────────────────────

export const insightTurnSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string().max(2000),
})
export type InsightTurn = z.infer<typeof insightTurnSchema>

/** The chart on screen, as numbers — so "why is X so high?" can be answered from what is drawn. */
export const insightResultDigestSchema = z.object({
  rows: z
    .array(
      z.object({
        label: z.string().max(200),
        values: z.record(z.string().max(200), z.number().nullable()),
      }),
    )
    .max(60),
})
export type InsightResultDigest = z.infer<typeof insightResultDigestSchema>

export const INSIGHT_PROMPT_MAX = 1000
/** A Library selection is sent as ids; past this many, "select all" should simply be the view. */
export const INSIGHT_SELECTION_MAX = 5000

/**
 * Which rows the session works over. The SERVER reads them — the same cached stream the page
 * shows, for the same load window — so the model's reach is the library cache and nothing else:
 * - `view` (Analytics): the window plus the page's filters, applied with the pages' own
 *   `applyStreamFilters`;
 * - `selection` (Library): the window, narrowed to the reader's selected ids.
 */
export const insightScopeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('view'),
    window: streamHitsQuerySchema,
    filters: streamFiltersSchema,
  }),
  z.object({
    kind: z.literal('selection'),
    window: streamHitsQuerySchema,
    hitIds: z.array(z.string().min(1).max(100)).min(1).max(INSIGHT_SELECTION_MAX),
  }),
])
export type InsightScope = z.input<typeof insightScopeSchema>

/** A version as the model is told about it: 1-based as the console shows it ("v3"), its title,
 *  what it changed, and the version it was made from. */
export const insightVersionRefSchema = z.object({
  n: z.number().int().min(1),
  title: z.string().max(200),
  note: z.string().max(160),
  parent: z.number().int().min(1).nullable(),
})
export type InsightVersionRef = z.infer<typeof insightVersionRefSchema>

export const insightRequestSchema = z.object({
  prompt: z.string().trim().min(1).max(INSIGHT_PROMPT_MAX),
  /** Earlier turns of this session, oldest first. */
  history: z.array(insightTurnSchema).max(16).default([]),
  scope: insightScopeSchema,
  /** The chart the reader is iterating on, if any — "make it a line chart" edits THIS. */
  current: insightSpecSchema.nullable().default(null),
  /** The conversation's versions, as the console lists them, and which one is on screen — so "go
   *  back to the one without Instacart" can name a version, and the model reads later versions in
   *  the transcript as later, not as what is drawn. */
  versions: z.array(insightVersionRefSchema).max(60).default([]),
  onScreen: z.number().int().min(1).nullable().default(null),
  /** The dashboard tab the reader is looking at (Analytics), so Derek can read its modules. */
  dashboardId: z.string().max(100).nullable().default(null),
  /** The module the reader opened Derek on — "make these a donut" means this one. */
  focusModuleId: z.string().max(100).nullable().default(null),
})
export type InsightRequest = z.input<typeof insightRequestSchema>
export type InsightRequestParsed = z.infer<typeof insightRequestSchema>

/** A question to Derek on a page that is not Analytics or the Library: what is on the screen, as
 *  text the browser read from the page, with the conversation so far. */
export const INSIGHT_PAGE_TEXT_MAX = 12_000
export const insightPageRequestSchema = z.object({
  prompt: z.string().trim().min(1).max(INSIGHT_PROMPT_MAX),
  history: z.array(insightTurnSchema).max(16).default([]),
  page: z.object({
    path: z.string().max(300),
    title: z.string().max(200),
    text: z.string().max(INSIGHT_PAGE_TEXT_MAX),
  }),
})
export type InsightPageRequest = z.input<typeof insightPageRequestSchema>

/** One tool call the assistant made on the way to its answer — shown in the console as a trace
 *  line, the way a terminal shows the commands it ran. */
export const insightStepSchema = z.object({
  tool: z.string(),
  summary: z.string(),
})
export type InsightStep = z.infer<typeof insightStepSchema>

/**
 * A change Coverage Lab PROPOSES — never makes. The console shows it with a button that opens the
 * Library's own flow for it, prefilled: the Remove confirmation (`POST /streams/:id/removals`) or
 * the Add articles dialog (`POST /articles/:id`), so the write is the one a person makes there,
 * with its permission, its preview fence and its audit. What the model proposed is resolved on
 * the server first: a removal names only articles in scope, an addition only links the analyst
 * pasted and topics this team has.
 */
export const INSIGHT_REMOVAL_REASONS = removeStreamHitsSchema.shape.reason.unwrap().options
export const insightActionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('remove'),
    hitIds: z.array(z.string()).min(1).max(STREAM_HITS_CAP),
    /** The biggest few, to show what the removal is before anyone confirms it. */
    sample: z
      .array(
        z.object({
          id: z.string(),
          headline: z.string(),
          outlet: z.string(),
          publishedAt: z.string(),
        }),
      )
      .max(10),
    reason: z.enum(INSIGHT_REMOVAL_REASONS).nullable(),
    /** Why, in the analyst's terms — "The Instacart giveaway pickups". */
    why: z.string().max(300),
  }),
  z.object({
    kind: z.literal('add'),
    articles: z
      .array(z.object({ url: z.string().url().max(2000), title: z.string().max(1000).nullable() }))
      .min(1)
      .max(ATTACH_ARTICLES_MAX),
    /** This team's topics to attach them under; the dialog asks when there are none. */
    topicIds: z.array(z.uuid()).max(50),
    why: z.string().max(300),
  }),
])
export type InsightAction = z.infer<typeof insightActionSchema>

export const insightResponseSchema = z.object({
  /** What Coverage Lab says in the console: the finding, in a sentence or two. */
  reply: z.string(),
  /** The chart to draw — null when the turn was a question answered in words. */
  spec: insightSpecSchema.nullable(),
  /** More charts, when several were asked for ("build me a dashboard") — saved together. */
  more: z.array(insightSpecSchema).max(5),
  /** A written briefing, when one was asked for ("summarise this coverage"). */
  summary: insightSummaryOutSchema.nullable(),
  /** Up to three next steps, phrased as the analyst would ask them — buttons under the answer. */
  suggestions: z.array(z.string().max(60)).max(3),
  /** What the new version changed, in a few words ("Without the Instacart story") — its name in
   *  the version list. Null when there is no new chart, or the model gave none. */
  change: z.string().max(160).nullable().default(null),
  /** "Go back to v3" / "the one without Instacart": an earlier version to put on screen instead
   *  of drawing a new one (1-based). */
  showVersion: z.number().int().min(1).nullable().default(null),
  /** A change to the coverage it proposes, for the analyst to confirm in the Library's own flow. */
  action: insightActionSchema.nullable().default(null),
  /** One question back, when the request genuinely forks and the coverage cannot settle it — with
   *  the choices as buttons. Rare: most of the time Derek takes the more useful reading. */
  clarify: z
    .object({ question: z.string().max(300), options: z.array(z.string().max(80)).min(2).max(4) })
    .nullable()
    .default(null),
  /** A build-section module drawn another way ("the clusters as a donut, top 5"): which module on
   *  the dashboard on screen, and its whole new view — checked against that dashboard by the server. */
  moduleEdit: z
    .object({ blockId: z.string().max(100), section: sectionViewSchema })
    .nullable()
    .default(null),
  /** What it looked at on the way, in plain words ("Checked the numbers"). */
  steps: z.array(insightStepSchema),
  /** Rows in scope, as the server resolved them. */
  scopeCount: z.number().int().min(0),
})
export type InsightResponse = z.infer<typeof insightResponseSchema>

export const insightPageResponseSchema = z.object({
  reply: z.string(),
  suggestions: z.array(z.string().max(60)).max(3),
  steps: z.array(insightStepSchema),
})
export type InsightPageResponse = z.infer<typeof insightPageResponseSchema>
